﻿import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CheckOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";

interface Role {
  key: string;
  label: string;
  isNewSpeaker?: boolean;
  speakCount?: number;
  isSeriesSpeaker?: boolean;
}

interface RoleDropdownProps {
  roles: Role[];
  speakerLineCounts?: Record<string, number>;
  currentSpeakerId?: number;
  selectedRole: string;
  onRoleSelect: (role: string, speakerId?: string) => void;
  onNewSpeaker?: (speakerName?: string) => void | Promise<void>;
  showNewSpeaker?: boolean;
  editingRoleKey?: string | null;
  editingRoleName?: string;
  onStartEditRole?: (roleKey: string, currentName: string) => void;
  onSaveRoleEdit?: (roleKey: string) => Promise<void>;
  onCancelRoleEdit?: () => void;
  onEditingRoleNameChange?: (name: string) => void;
  onDeleteRole?: (roleKey: string) => void;
  isSeriesSpeaker?: boolean;
  disabled?: boolean;
  onDisabledClick?: () => void;
  /** 是否为校对页（控制删除按钮显示） */
  isFromPure?: boolean;
  /** 批量应用模式 */
  batchMode?: boolean;
  onBatchModeChange?: (v: boolean) => void;
}

const roleColors = [
  { background: '#00aeb41a', color: '#00b5bd' },
  { background: '#7271F117', color: '#7271F1' },
  { background: '#FCCA0721', color: '#DFB20A' },
  { background: '#ff4d4f2e', color: '#ff4d4f' },
  { background: '#e9f5e9', color: '#3b9f3c' },
  { background: '#06a2d11a', color: '#06a2d1' },
  { background: '#ffb36612', color: '#f7a552' },
];

const RoleDropdown: React.FC<RoleDropdownProps> = ({
  roles,
  speakerLineCounts,
  currentSpeakerId,
  selectedRole,
  onRoleSelect,
  onNewSpeaker,
  showNewSpeaker = true,
  editingRoleKey,
  editingRoleName = '',
  onStartEditRole,
  onSaveRoleEdit,
  onCancelRoleEdit,
  onEditingRoleNameChange,
  onDeleteRole,
  disabled = false,
  onDisabledClick,
  isFromPure = false,
  batchMode = false,
  onBatchModeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubMenuKey, setOpenSubMenuKey] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [isAddingSpeaker, setIsAddingSpeaker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const editingRoleKeyRef = useRef(editingRoleKey);
  const onSaveRoleEditRef = useRef(onSaveRoleEdit);

  useEffect(() => {
    editingRoleKeyRef.current = editingRoleKey;
    onSaveRoleEditRef.current = onSaveRoleEdit;
  });

  const getSpeakerIdFromRoleKey = (roleKey: string) => {
    return roleKey.match(/^(?:speaker_|series_speaker_)(\d+)$/)?.[1];
  };

  const getRoleStyle = (roleName: string) => {
    const roleIndex = roles.findIndex(role => role.label === roleName);
    if (roleIndex === -1) return roleColors[0];
    return roleColors[roleIndex % roleColors.length];
  };

  const closeDropdown = useCallback(() => {
    setOpenSubMenuKey(null);
    setSubmenuPosition(null);
    if (editingRoleKeyRef.current) {
      onSaveRoleEditRef.current?.(editingRoleKeyRef.current);
    }
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      const inSubmenu = submenuRef.current?.contains(target);
      if (!inTrigger && !inMenu && !inSubmenu) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const containerRect = dropdownRef.current?.getBoundingClientRect();

      if (containerRect) {
        menuRef.current.style.top = `${triggerRect.bottom - containerRect.top + 4}px`;
        menuRef.current.style.left = `${triggerRect.left - containerRect.left}px`;
      }
    }
  }, [isOpen]);

  const updateSubmenuPosition = useCallback(() => {
    if (!menuRef.current || !openSubMenuKey) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const activeItem = menuRef.current.querySelector('.role-dropdown-item-active') as HTMLElement | null;
    const top = activeItem ? activeItem.getBoundingClientRect().top : menuRect.top;
    setSubmenuPosition({ left: menuRect.right + 4, top });
  }, [openSubMenuKey]);

  useEffect(() => {
    if (!openSubMenuKey || !menuRef.current) {
      setSubmenuPosition(null);
      return;
    }
    updateSubmenuPosition();
  }, [openSubMenuKey, updateSubmenuPosition]);

  const closeSubmenu = useCallback(() => {
    setOpenSubMenuKey(null);
    setSubmenuPosition(null);
  }, []);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu || !openSubMenuKey) return;
    menu.addEventListener('scroll', closeSubmenu);
    return () => menu.removeEventListener('scroll', closeSubmenu);
  }, [openSubMenuKey, closeSubmenu]);

  const handleSaveAndSwitchEdit = async (role: Role) => {
    if (editingRoleKey) {
      try {
        await onSaveRoleEdit?.(editingRoleKey);
      } catch (err) {
        console.error('保存说话人编辑失败:', err);
        return;
      }
    }
    onStartEditRole?.(role.key, role.label);
  };

  const handleRoleClick = (role: Role, e?: React.MouseEvent) => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }

    if (editingRoleKey && editingRoleKey !== role.key) {
      e?.stopPropagation();
      handleSaveAndSwitchEdit(role);
      return;
    }

    if (editingRoleKey === role.key) {
      e?.stopPropagation();
      return;
    }

    const speakerId = role.key.startsWith('speaker_') ? role.key.replace('speaker_', '') : null;
    onRoleSelect(role.label, speakerId || undefined);
    setIsOpen(false);
  };

  const handleTriggerClick = () => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }

    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    setOpenSubMenuKey(null);
    setSubmenuPosition(null);
    if (nextOpen) {
      setSearchQuery('');
    }
  };

  const handleAddSpeaker = async () => {
    if (!newSpeakerName.trim() || isAddingSpeaker) return;

    setIsAddingSpeaker(true);
    try {
      await onNewSpeaker?.(newSpeakerName.trim());
      setNewSpeakerName('');
    } finally {
      setIsAddingSpeaker(false);
    }
  };

  const currentSpeakerIdText = currentSpeakerId != null ? String(currentSpeakerId) : undefined;
  const currentRole = currentSpeakerIdText
    ? roles.find(role => getSpeakerIdFromRoleKey(role.key) === currentSpeakerIdText)
    : roles.find(role => role.label === selectedRole);
  const currentStyle = getRoleStyle(currentRole?.label ?? selectedRole);
  const currentSpeakerLineCount = currentRole?.speakCount ?? speakerLineCounts?.[selectedRole];
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredRoles = normalizedSearch
    ? roles.filter(role => role.label.toLowerCase().includes(normalizedSearch))
    : roles;

  return (
    <div className="speaker-role-dropdown" ref={dropdownRef}>
      <div
        ref={triggerRef}
        className={`sp-trigger ${isOpen ? 'is-open' : ''} ${currentRole?.isSeriesSpeaker ? 'has-global-tag' : ''}`}
        onClick={handleTriggerClick}
        style={{
          backgroundColor: currentStyle.background,
          color: currentStyle.color,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span className="sp-trigger-content">
          <span
            className="sp-trigger-name-text"
            title={currentSpeakerLineCount !== undefined ? `${selectedRole} (${currentSpeakerLineCount})` : selectedRole}
          >
            {selectedRole}
            {currentSpeakerLineCount !== undefined && <> ({currentSpeakerLineCount})</>}
          </span>
          {currentRole?.isSeriesSpeaker && (
            <span className="sp-trigger-tag-global">全局</span>
          )}
        </span>
        <DownOutlined className={`sp-trigger-chevron ${isOpen ? 'is-open' : ''}`} />
      </div>

      {isOpen && (
        <>
          <div ref={menuRef} className="speaker-custom-dropdown show">
            <div className="sp-dd-header">说话人</div>

            <div className="sp-dd-search">
              <SearchOutlined className="sp-dd-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索说话人..."
                className="sp-dd-search-input"
              />
            </div>

            <div className="sp-dd-list">
              {filteredRoles.length === 0 ? (
                <div className="sp-dd-empty">
                  {searchQuery ? '无匹配结果' : '暂无说话人'}
                </div>
              ) : (
                filteredRoles.map((role) => {
                  const roleSpeakerId = getSpeakerIdFromRoleKey(role.key);
                  const isSelected = currentSpeakerIdText
                    ? roleSpeakerId === currentSpeakerIdText
                    : false;
                  const isEditing = editingRoleKey === role.key;

                  return (
                    <div
                      key={role.key}
                      className={`sp-dd-option role-dropdown-item ${isSelected ? 'selected' : ''} ${openSubMenuKey === role.key ? 'role-dropdown-item-active' : ''}`}
                      onClick={(e) => handleRoleClick(role, e)}
                    >
                      <div className="sp-dd-info">
                        {isEditing ? (
                          <input
                            className="sp-dd-edit-input"
                            value={editingRoleName}
                            autoFocus
                            onChange={(e) => onEditingRoleNameChange?.(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={async () => {
                              await onSaveRoleEdit?.(role.key);
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                await onSaveRoleEdit?.(role.key);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                onCancelRoleEdit?.();
                              }
                            }}
                          />
                        ) : (
                          <>
                            <div className="sp-dd-name">
                              <span className="sp-dd-name-text" title={role.label}>
                                {role.label}
                              </span>
                              {role.speakCount !== undefined && (
                                <span className="sp-dd-count">({role.speakCount})</span>
                              )}
                              {role.isSeriesSpeaker && (
                                <span className="sp-dd-tag-global">全局</span>
                              )}
                            </div>
                            {role.speakCount !== undefined && (
                              <div className="sp-dd-meta">出现 {role.speakCount} 次</div>
                            )}
                          </>
                        )}
                      </div>

                      {!isEditing && onStartEditRole && (
                        <button
                          type="button"
                          className="sp-dd-act sp-dd-rename"
                          title="重命名"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveAndSwitchEdit(role);
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                        </button>
                      )}
                      {!isEditing && onDeleteRole && isFromPure && (
                        <button
                          type="button"
                          className="sp-dd-act sp-dd-delete"
                          title="删除"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteRole(role.key);
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                      {isSelected && !isEditing && (
                        <CheckOutlined className="sp-dd-check" />
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {showNewSpeaker && (
              <div className="sp-dd-add-area">
                <div className="sp-dd-add-form">
                  <input
                    type="text"
                    value={newSpeakerName}
                    onChange={(e) => setNewSpeakerName(e.target.value)}
                    placeholder="说话人名称"
                    className="sp-add-name"
                    maxLength={20}
                  />
                  <div className="sp-dd-add-form-actions">
                    {isFromPure && onBatchModeChange && (
                      <label className="sp-dd-batch" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={batchMode}
                          onChange={() => onBatchModeChange(!batchMode)}
                        />
                        <span>批量应用到所有此说话人</span>
                      </label>
                    )}
                    <div className="sp-dd-btns">
                      <button
                        type="button"
                        className="btn-cancel sp-add-cancel"
                        onClick={() => {
                          setNewSpeakerName('');
                          setIsOpen(false);
                        }}
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        className="btn-confirm sp-add-confirm"
                        onClick={handleAddSpeaker}
                        disabled={!newSpeakerName.trim() || isAddingSpeaker}
                      >
                        {isAddingSpeaker ? '添加中...' : '确认添加'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>

          {openSubMenuKey && submenuPosition && (() => {
            const role = roles.find(r => r.key === openSubMenuKey);
            if (!role) return null;
            return (
              <div
                ref={submenuRef}
                className="role-submenu role-submenu-right"
                role="menu"
                style={{
                  position: 'fixed',
                  left: submenuPosition.left,
                  top: submenuPosition.top,
                  zIndex: 10001,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="role-submenu-item"
                  role="menuitem"
                  onClick={() => {
                    handleSaveAndSwitchEdit(role);
                    setOpenSubMenuKey(null);
                  }}
                >
                  重命名
                </div>
                <div
                  className="role-submenu-item role-submenu-item-danger"
                  role="menuitem"
                  onClick={() => {
                    onDeleteRole?.(role.key);
                    setOpenSubMenuKey(null);
                    setIsOpen(false);
                  }}
                >
                  删除
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default RoleDropdown;
